// The staff side of client work: every request, program question and follow-up as a thread (cloud 0005), with the engine's
// status, filters with counts, a conversation view (firm / client / system messages, attachments, events; agent drafts violet and
// marked as drafts), and every act as one catalogue command through the inbox (threadActs.ts). Used by the engagement's Client tab
// (one engagement) and the firm's "Clients & requests" page (all engagements you can see). Nothing here marks anything done:
// the list refreshes from the engine's read models (Realtime `threads`).
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ActionDrawer, type ActionIntent } from "../../app/ActionDrawer";
import { useApp } from "../../app/context";
import { useEngine } from "../../app/engine";
import { AgentChip, Button, EmptyState, ErrorState, LoadState, LoadingRows, Modal, StatusPill, Thread, inputCls } from "../../components";
import type { Message, Thread as ThreadRow, ThreadAttachment, Upload } from "../../data/types";
import { mayRequest } from "../../lib/commands";
import { fmtDate, fmtStamp, plural, titleCase } from "../../lib/format";
import { useLoad, useRealtime } from "../../lib/hooks";
import { statusOf } from "../../lib/status";
import { STATUS_ORDER, bulkRemind, bulkSend, messageActs, threadActs, type ThreadAct } from "./threadActs";

export type ThreadListRow = ThreadRow & { client: string };
const KIND_LABEL: Record<string, string> = { request: "Request", question: "Program question", followup: "Follow-up" };
/** Engine states in which the next move is the firm's (threads.py: a response or an N/A request waits for a person). */
const FIRM_TURN = ["responded", "na_requested"];

export function ThreadWorkspace({ engagements, showClient, toolbar }: { engagements: Array<{ id: string; name: string }>; showClient: boolean; toolbar?: ReactNode }) {
  const { ds } = useApp();
  const key = engagements.map((e) => e.id).join();
  const threads = useLoad(async () => {
    const per = await Promise.all(engagements.map(async (e) => (await ds.table(e.id, "client_threads")).map((t): ThreadListRow => ({ ...t, client: e.name }))));
    return per.flat();
  }, [ds, key]);
  const [open, setOpen] = useState<{ eid: string; id: string } | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [kind, setKind] = useState<string>("");
  const [firmTurn, setFirmTurn] = useState(false);
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulk, setBulk] = useState<"send" | "remind" | null>(null);
  const [intent, setIntent] = useState<ActionIntent | null>(null);
  useRealtime(ds, "threads", (e) => { if (!e.engagementId || engagements.some((x) => x.id === e.engagementId)) threads.reload(); });

  const rows = threads.data ?? [];
  const base = rows.filter((t) => (!kind || t.kind === kind) && (!firmTurn || FIRM_TURN.includes(t.status))
    && (!q.trim() || `${t.subject} ${t.body} ${t.client}`.toLowerCase().includes(q.trim().toLowerCase())));
  const counts = useMemo(() => { const m = new Map<string, number>(); for (const t of base) m.set(t.status, (m.get(t.status) ?? 0) + 1); return m; }, [base]);
  const shown = base.filter((t) => !status || t.status === status);
  const rk = (t: ThreadRow) => `${t.engagementId}:${t.id}`;
  const sel = rows.filter((t) => picked.has(rk(t)));
  const current = open ? rows.find((t) => t.engagementId === open.eid && t.id === open.id) ?? null : null;
  const toggle = (t: ThreadRow) => setPicked((p) => { const n = new Set(p); if (n.has(rk(t))) n.delete(rk(t)); else n.add(rk(t)); return n; });

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <label className="sr-only" htmlFor="thread-search">Search threads</label>
        <input id="thread-search" className={`${inputCls} w-full sm:w-56`} placeholder="Search subject or client" value={q} onChange={(e) => setQ(e.target.value)} />
        <label className="sr-only" htmlFor="thread-kind">Kind</label>
        <select id="thread-kind" className={`${inputCls} sm:w-48`} value={kind} onChange={(e) => setKind(e.target.value)}>
          <option value="">All kinds</option><option value="request">Requests</option><option value="question">Program questions</option><option value="followup">Follow-ups</option>
        </select>
        <label className="inline-flex items-center gap-1.5 text-sm"><input type="checkbox" checked={firmTurn} onChange={(e) => setFirmTurn(e.target.checked)} />Waiting on the firm</label>
        <span className="flex-1" />
        {toolbar}
      </div>
      <div role="group" aria-label="Filter by status" className="flex flex-wrap gap-1.5">
        <Chip on={status === null} onClick={() => setStatus(null)} label="All" count={base.length} />
        {STATUS_ORDER.filter((s) => counts.has(s)).concat([...counts.keys()].filter((s) => !STATUS_ORDER.includes(s))).map((s) => (
          <Chip key={s} on={status === s} onClick={() => setStatus(status === s ? null : s)} label={statusOf(s).label} count={counts.get(s) ?? 0} />
        ))}
      </div>
      {sel.length > 0 && (
        <div role="region" aria-label="Bulk actions" className="flex flex-wrap items-center gap-2 rounded-md border border-line bg-sunken px-3 py-2 text-sm">
          <span>{plural(sel.length, "thread")} selected</span>
          <Button size="sm" kind="primary" disabled={!bulkSend(sel).length} onClick={() => setBulk("send")}>Send drafts ({bulkSend(sel).length})</Button>
          <Button size="sm" disabled={!bulkRemind(sel).length} onClick={() => setBulk("remind")}>Remind ({bulkRemind(sel).length})</Button>
          <Button size="sm" kind="ghost" onClick={() => setPicked(new Set())}>Clear</Button>
        </div>
      )}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className={`min-w-0 ${current ? "hidden lg:block" : ""}`}>
          {threads.error ? <ErrorState error={threads.error} onRetry={threads.reload} /> : threads.data === undefined ? <LoadingRows rows={5} label="Loading threads" /> : (
            <ul aria-label="Client threads" className="divide-y divide-line rounded-md border border-line bg-raised">
              {shown.length === 0 && <li className="p-4 text-sm text-muted">{rows.length ? "No thread matches these filters." : "No client threads yet. Draft a request or a question to start one."}</li>}
              {shown.map((t) => (
                <li key={rk(t)} className={`flex items-start gap-2 px-3 py-2 ${current && rk(current) === rk(t) ? "bg-brand/10" : ""}`}>
                  <input type="checkbox" className="mt-1 h-4 w-4" aria-label={`Select ${t.subject}`} checked={picked.has(rk(t))} onChange={() => toggle(t)} />
                  <button type="button" onClick={() => setOpen({ eid: t.engagementId, id: t.id })} aria-current={current && rk(current) === rk(t) ? "true" : undefined}
                    className="min-w-0 flex-1 rounded text-left text-sm hover:bg-sunken">
                    <span className="flex flex-wrap items-center gap-1.5">
                      <StatusPill state={t.status} />
                      <span className="text-xs text-muted">{KIND_LABEL[t.kind] ?? titleCase(t.kind)}{t.requestId ? ` #${t.requestId}` : ""}{showClient ? ` · ${t.client}` : ""}</span>
                      {t.draftedByAgent && <AgentChip />}
                    </span>
                    <span className="mt-0.5 block truncate font-medium">{t.subject}</span>
                    <span className="block text-xs text-muted">
                      {t.due ? `Due ${fmtDate(t.due)}` : "No due date"}{t.reminders ? ` · ${plural(t.reminders, "reminder")}` : ""}{!t.clientVisible ? " · not visible to the client" : ""}
                    </span>
                    {t.returnReason && t.status === "returned" && <span className="block truncate text-xs text-client">Returned: {t.returnReason}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className={`min-w-0 ${current ? "" : "hidden lg:block"}`}>
          {current ? <ThreadDetail t={current} showClient={showClient} onBack={() => setOpen(null)} onAct={(a) => setIntent({ command: a.command, engagementId: current.engagementId, fixed: a.fixed, initial: a.initial, title: a.title, hide: a.hide })} />
            : <EmptyState title="Choose a thread">Its conversation, attachments and the acts open to you appear here.</EmptyState>}
        </div>
      </div>
      <BulkConfirm kind={bulk} threads={sel} onClose={() => setBulk(null)} onDone={() => { setBulk(null); setPicked(new Set()); }} />
      <ActionDrawer intent={intent} onClose={() => setIntent(null)} />
    </div>
  );
}

function Chip({ on, onClick, label, count }: { on: boolean; onClick: () => void; label: string; count: number }) {
  return (
    <button type="button" aria-pressed={on} onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs ${on ? "border-brand bg-brand/10 font-semibold text-brand" : "border-line hover:bg-sunken"}`}>
      {label}<span className="tabular-nums text-muted">{count}</span>
    </button>
  );
}

/** An act button: disabled with the reason when the catalogue says this person may not ask for it; human-only acts say so. */
export function ActButton({ act, onAct, size = "sm" }: { act: ThreadAct; onAct: (a: ThreadAct) => void; size?: "sm" | "md" }) {
  const { catalogue, me } = useApp();
  const spec = catalogue.get(act.command);
  const ok = mayRequest(spec, me, "engagement");
  return (
    <Button size={size} kind={act.tone === "primary" ? "primary" : act.tone === "danger" ? "danger" : "secondary"} disabled={!ok.ok} title={ok.ok ? (spec?.humanOnly ? "Your own act" : spec?.summary) : ok.reason ?? undefined}
      onClick={() => onAct(act)} data-command={act.command}>
      {act.label}{!ok.ok && <span className="sr-only"> ({ok.reason})</span>}
    </Button>
  );
}

function ThreadDetail({ t, showClient, onBack, onAct }: { t: ThreadListRow; showClient: boolean; onBack: () => void; onAct: (a: ThreadAct) => void }) {
  const { ds } = useApp();
  const detail = useLoad(async () => {
    const [messages, attachments, uploads] = await Promise.all([
      ds.table(t.engagementId, "client_messages"), ds.table(t.engagementId, "client_attachments").catch(() => [] as ThreadAttachment[]),
      t.requestId !== null ? ds.table(t.engagementId, "pbc_uploads").catch(() => [] as Upload[]) : Promise.resolve([] as Upload[]),
    ]);
    return { messages: messages.filter((m) => m.thread === t.id).sort((a, b) => a.at.localeCompare(b.at)), attachments: attachments.filter((a) => a.thread === t.id),
      uploads: uploads.filter((u) => u.requestId === t.requestId) };
  }, [ds, t.engagementId, t.id, t.requestId]);
  useRealtime(ds, "threads", (e) => { if (e.engagementId === t.engagementId) detail.reload(); });
  const drafts = new Set(t.agentDrafts);
  const tint = (m: Message): Message => (m.engineId && drafts.has(m.engineId) && !m.agent ? { ...m, agent: "agent" } : m);
  const acts = threadActs(t, { messages: detail.data?.messages, uploads: detail.data?.uploads });
  return (
    <section aria-label={`Thread: ${t.subject}`} className="space-y-3 rounded-md border border-line bg-raised p-3">
      <button type="button" className="text-sm text-brand hover:underline lg:hidden" onClick={onBack}>← All threads</button>
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill state={t.status} />
          <span className="text-xs text-muted">{KIND_LABEL[t.kind] ?? titleCase(t.kind)} {t.kind === "request" && t.requestId ? `#${t.requestId}` : `thread ${t.id}`}{showClient ? ` · ${t.client}` : ""}</span>
          {t.draftedByAgent && <AgentChip name="drafted" />}
          {!t.clientVisible && <StatusPill state="info" label="Not visible to the client" />}
        </div>
        <h3 className="text-base font-semibold">{t.subject}</h3>
        <dl className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted">
          <div><dt className="inline">Due </dt><dd className="inline">{fmtDate(t.due)}</dd></div>
          <div><dt className="inline">Sent </dt><dd className="inline">{t.sentAt ? `${fmtStamp(t.sentAt)}${t.sentBy ? ` by ${t.sentBy}` : ""}` : "not yet"}</dd></div>
          <div><dt className="inline">Reminders </dt><dd className="inline tabular-nums">{t.reminders}</dd></div>
          {t.openedBy && <div><dt className="inline">Opened by </dt><dd className="inline">{t.openedBy}</dd></div>}
          {t.workpaper && <div><dt className="inline">Workpaper </dt><dd className="inline"><Link className="text-brand hover:underline" to={`/e/${t.engagementId}/details/wp/${t.workpaper}`}>{t.workpaper}</Link></dd></div>}
          {t.anchorKind && t.anchorKind !== "request" && <div><dt className="inline">About </dt><dd className="inline font-mono">{t.anchorKind}{t.anchorId ? `:${t.anchorId.slice(0, 16)}` : ""}</dd></div>}
          {t.closedAt && <div><dt className="inline">Closed </dt><dd className="inline">{fmtStamp(t.closedAt)}{t.closedBy ? ` by ${t.closedBy}` : ""}{t.resolution ? ` — ${t.resolution}` : ""}</dd></div>}
        </dl>
      </header>
      <div className="rounded-md border border-line bg-sunken px-3 py-2 text-sm"><span className="block text-xs text-muted">{t.kind === "request" ? "Requested" : "Asked"}</span><p className="whitespace-pre-wrap">{t.body}</p></div>
      {t.returnReason && t.status === "returned" && <p className="rounded border border-client-line bg-client-bg px-2 py-1 text-sm text-client">Returned to the client: {t.returnReason}</p>}
      {t.naReason && <blockquote className="rounded border border-client-line bg-client-bg px-2 py-1 text-sm"><span className="block text-xs text-client">The client says it does not apply (client's statement)</span>{t.naReason}</blockquote>}
      <LoadState state={detail}>
        {(d) => (
          <>
            {(d.attachments.length > 0 || d.uploads.length > 0) && (
              <div>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Files from the client</h4>
                <ul className="divide-y divide-line rounded border border-line text-sm">
                  {[...d.uploads.map((u) => ({ id: u.id, name: u.fileName, status: u.status, note: u.engineNote, at: u.createdAt, sha: u.sha256 })),
                    ...d.attachments.map((a) => ({ id: a.id, name: a.fileName, status: a.status, note: a.engineNote, at: a.createdAt, sha: a.sha256 }))].map((f) => (
                    <li key={f.id} className="flex flex-wrap items-center gap-2 px-2 py-1.5">
                      <StatusPill state={f.status} /><span className="min-w-0 flex-1 break-words">{f.name}</span>
                      <span className="font-mono text-[11px] text-muted" title={f.sha}>{f.sha.slice(0, 12)}</span><span className="text-xs text-muted">{fmtStamp(f.at)}</span>
                      {f.note && <span className="basis-full text-xs text-muted">{f.note}</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <Thread viewer="firm" messages={d.messages.map(tint)} renderActions={(m) => {
              const ma = messageActs(t, m);
              return ma.length ? <div className="mt-1.5 flex flex-wrap gap-1.5">{ma.map((a) => <ActButton key={a.id} act={a} onAct={onAct} />)}</div> : null;
            }} />
          </>
        )}
      </LoadState>
      <div role="group" aria-label="Acts on this thread" className="flex flex-wrap gap-2 border-t border-line pt-3">
        {acts.length === 0 ? <p className="text-sm text-muted">This thread is closed; nothing is open to act on.</p> : acts.map((a) => <ActButton key={a.id} act={a} onAct={onAct} />)}
      </div>
    </section>
  );
}

/** Bulk send / remind: shows every inbox row it will create (one catalogue command each) before the person confirms. */
function BulkConfirm({ kind, threads, onClose, onDone }: { kind: "send" | "remind" | null; threads: ThreadRow[]; onClose: () => void; onDone: () => void }) {
  const request = useEngine();
  const [busy, setBusy] = useState(false);
  const rows = kind === "send" ? bulkSend(threads) : kind === "remind" ? bulkRemind(threads) : [];
  return (
    <Modal open={!!kind} onClose={onClose} label={kind === "send" ? "Send drafts to the client" : "Remind the client"}>
      <div className="space-y-3 p-4">
        <h2 className="text-base font-semibold">{kind === "send" ? "Send to the client" : "Send reminders"}</h2>
        {kind === "send" && <p className="rounded-md border border-waiting-line bg-waiting-bg px-3 py-2 text-xs text-waiting">Sending is your own act: text leaves the firm in your name. The engine records each one.</p>}
        <ul className="list-inside list-disc text-sm">{rows.map((r, i) => <li key={i}><span className="font-mono text-xs text-muted">{r.command}</span> {r.what}</li>)}</ul>
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Cancel</Button>
          <Button kind="primary" disabled={busy || !rows.length} onClick={async () => {
            setBusy(true);
            for (const r of rows) await request(r);
            setBusy(false); onDone();
          }}>{kind === "send" ? `Send ${rows.length}` : `Remind ${rows.length}`}</Button>
        </div>
      </div>
    </Modal>
  );
}
